import { FaExternalLinkAlt, FaArrowRight } from 'react-icons/fa';

const CaseStudies = () => {
  const caseStudies = [
    {
      title: 'E-commerce Platform Redesign',
      category: 'E-commerce',
      description: 'Complete redesign and development of a modern e-commerce platform that increased sales by 150% and improved user experience.',
      imageUrl: 'https://images.unsplash.com/photo-1661956602116-aa6865609028?auto=format&fit=crop&q=80&w=800',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'],
      results: ['150% increase in sales', '40% better conversion rate', '60% faster loading time'],
      color: 'from-gray-800 to-black'
    },
    {
      title: 'AI-Powered Analytics Dashboard',
      category: 'AI & Machine Learning',
      description: 'Development of an intelligent analytics dashboard with machine learning capabilities for data-driven business insights.',
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800',
      technologies: ['Python', 'TensorFlow', 'React', 'PostgreSQL'],
      results: ['90% faster data processing', 'Real-time insights', 'Predictive analytics'],
      color: '[#22446b]'
    },
    {
      title: 'Mobile Banking Application',
      category: 'Mobile Experience',
      description: 'Secure and user-friendly mobile banking app with advanced features and biometric authentication.',
      imageUrl: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=800',
      technologies: ['React Native', 'Node.js', 'AWS', 'Blockchain'],
      results: ['500K+ downloads', '4.8 app store rating', 'Zero security incidents'],
      color: '[#22446b]'
    },
    {
      title: 'Digital Marketing Campaign',
      category: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategy that boosted online presence and generated qualified leads.',
      imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
      technologies: ['Google Ads', 'Facebook Ads', 'SEO', 'Analytics'],
      results: ['300% increase in leads', '200% ROI improvement', '80% brand awareness boost'],
      color: '[#22446b]'
    }
  ];

  return (
    <section className="section-padding bg-tekone-light-bg font-opensans">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#22446b] to-[#338edc] border border-transparent rounded-full text-white text-sm font-medium mb-4">
              <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
              Latest Projects
          </div>
          <h2 className="text-4xl lg:text-5xl font-rajdhani font-bold text-[#22446b] mb-6">
            Case <span className="text-transparent bg-clip-text bg-[#22446b]">Studies</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our recent launched projects available in the market. Explore how we've helped 
            businesses achieve their digital transformation goals.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {caseStudies.map((study, index) => (
            <div 
              key={index}
              className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group border border-gray-100"
            >
              {/* Image Area */}
              <div className="h-72 relative overflow-hidden group-hover:shadow-inner">
                <img 
                  src={study.imageUrl} 
                  alt={study.title} 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Clean Hover Icon Overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-50 group-hover:scale-100">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                        <FaExternalLinkAlt className="text-white text-xl" />
                    </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-10">
                <div className="space-y-6">
                  {/* Category & Title */}
                  <div>
                    <span className="inline-block px-4 py-1.5 bg-gray-100 text-[#22446b] text-sm font-semibold rounded-full mb-4">
                      {study.category}
                    </span>
                    <h3 className="text-3xl font-rajdhani font-bold text-[#22446b] group-hover:text-gray-600 transition-colors duration-300">
                      {study.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-[#22446b] leading-relaxed">
                    {study.description}
                  </p>

                  {/* Technologies */}
                  <div>
                    <h4 className="font-rajdhani font-bold text-[#22446b] mb-3 text-lg">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {study.technologies.map((tech, techIndex) => (
                        <span 
                          key={techIndex}
                          className="px-3 py-1 bg-gray-50 border border-gray-200 text-[#22446b] text-sm font-medium rounded-lg hover:border-black/30 transition-colors"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Results */}
                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="font-rajdhani font-bold text-[#22446b] mb-3 text-lg">Key Results:</h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {study.results.map((result, resultIndex) => (
                        <li key={resultIndex} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-[#338edc] rounded-full mr-2"></div>
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Projects CTA */}
        {/* <div className="text-center mt-16">
          <button className="inline-flex items-center bg-transparent border-2 border-gray-200 text-black hover:text-white hover:bg-black hover:border-black font-rajdhani font-bold py-4 px-10 rounded-xl transition-all duration-300">
            View All Projects
            <FaArrowRight className="ml-2" />
          </button>
        </div> */}
      </div>
    </section>
  );
};

export default CaseStudies;